import { Query, useMutation, useQuery } from '@tanstack/react-query';

import { calculateRefetchInterval } from '@utils/time';

import {
    downloadBlobFile,
    getGeneralPredefinedPrompts,
    getNegotiatedWebsocketUrl,
    getPredefinedPromptsByRole,
} from './api';
import {
    NegotiateWebsocketUrlHubs,
    NegotiateWebsocketUrlResponse,
    PromptGroups,
    RolesWithPrompts,
} from './types';

export const sharedQueryKeys = {
    avatarSupportedLocales: 'avatarSupportedLocales',
    generalPredefinedPrompts: 'generalPredefinedPrompts',
    predefinedPromptsByRole: 'predefinedPromptsByRole',
} as const;

const queryKeys = {
    negotiateWebsocketUrl: 'negotiateWebsocketUrl',
} as const;

export const useNegotiateWebsocketUrl = (hub: NegotiateWebsocketUrlHubs) =>
    useQuery<NegotiateWebsocketUrlResponse, Error>({
        queryKey: [queryKeys.negotiateWebsocketUrl, hub],
        queryFn: () => getNegotiatedWebsocketUrl(hub),
        refetchInterval: (query: Query<NegotiateWebsocketUrlResponse, Error>) =>
            calculateRefetchInterval(query.state.data),
        refetchIntervalInBackground: true,
    });

export const useGeneralPredefinedPrompts = () =>
    useQuery<PromptGroups, Error>({
        queryKey: [sharedQueryKeys.generalPredefinedPrompts],
        queryFn: getGeneralPredefinedPrompts,
    });

export const usePredefinedPromptsByRole = () =>
    useQuery<RolesWithPrompts, Error>({
        queryKey: [sharedQueryKeys.predefinedPromptsByRole],
        queryFn: getPredefinedPromptsByRole,
    });

export const useDownloadBlobFile = (fileName: string) =>
    useMutation<Blob, Error, string>({
        mutationFn: downloadBlobFile,
        onSuccess: (blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        },
    });
